import {
  getDietaryIngredients,
  useCustomerDietaryRestrictions,
  type DietaryRestrictionsText,
} from '@hungryroot/dietary-restrictions';
import { PeriodicityTypes, type ProductDetail } from '@hungryroot/rest';
import {
  getNeveredIngredientNamesForProducts,
  useCustomerPeriodicities,
  useNeveredIngredientProductLookup,
} from '@hungryroot/rest-hooks';
import { productContainsNeverTag, useNeveredTagIds } from '@hungryroot/tags';
import {
  getDetailWarnings,
  type DetailWarning,
} from '../utils/getDetailWarnings';
import { getProductNeverTagName } from '../utils/getProductNeverTagName';
import { useExp195FoodProfileWarnings } from './useExp195FoodProfileWarnings';

export function getItemWarnings(
  dietaryProductIngredients: DietaryRestrictionsText[],
  periodicity: PeriodicityTypes | null,
  hasNever = false,
  neverTagNames: string[] = [],
  options?: { stackNeverWithDietary?: boolean }
): DetailWarning[] {
  const isNeverItem =
    periodicity === PeriodicityTypes.NEVER ||
    hasNever ||
    neverTagNames.length > 0;

  return getDetailWarnings({
    dietaryIngredients: dietaryProductIngredients,
    hasNever: isNeverItem,
    neverTagNames,
    scope: 'item',
    stackNeverWithDietary: options?.stackNeverWithDietary === true,
  });
}

export function resolveItemWarnings({
  dietaryIngredients,
  periodicity,
  hasNever,
  neverTagNames = [],
  stackNeverWithDietary = false,
}: {
  dietaryIngredients: DietaryRestrictionsText[] | undefined;
  periodicity: PeriodicityTypes | null | undefined;
  hasNever: boolean;
  neverTagNames?: string[];
  stackNeverWithDietary?: boolean;
}): DetailWarning[] {
  const hasNeverWarning =
    periodicity === PeriodicityTypes.NEVER ||
    hasNever === true ||
    neverTagNames.length > 0;

  if (dietaryIngredients !== undefined) {
    return getItemWarnings(
      dietaryIngredients,
      periodicity ?? null,
      hasNever,
      neverTagNames,
      { stackNeverWithDietary }
    );
  }

  if (hasNeverWarning)
    return getItemWarnings([], periodicity ?? null, hasNever, neverTagNames, {
      stackNeverWithDietary,
    });

  return [];
}

function useDietaryRestrictionText(product: ProductDetail | undefined) {
  const dietaryRestrictions = useCustomerDietaryRestrictions();
  const dietaryRestrictionsData =
    dietaryRestrictions.data?.dietary_restrictions;

  const cannotReturnData =
    !dietaryRestrictionsData || product?.tags === undefined;

  if (cannotReturnData) return undefined;
  const dietaryIngredients = getDietaryIngredients(
    product.tags,
    dietaryRestrictionsData
  );

  return dietaryIngredients;
}

function useProductPeriodicity(productId: number | undefined) {
  const cannotReturnData = productId === undefined;
  const periodicityResponse = useCustomerPeriodicities();

  if (cannotReturnData) return undefined;
  const periodicityDataForTargetProduct =
    periodicityResponse?.data?.results.find(
      (entry) => entry.product === productId
    );

  return periodicityDataForTargetProduct?.periodicity ?? null;
}

export function useItemWarning({
  product,
  enableFoodProfileNeverWarnings = false,
}: {
  product: ProductDetail | undefined;
  enableFoodProfileNeverWarnings?: boolean;
}): DetailWarning[] {
  const exp195Warnings = useExp195FoodProfileWarnings();
  const useNamedStackedWarnings =
    enableFoodProfileNeverWarnings && exp195Warnings.isEnabled;
  const useV12IngredientWarnings =
    enableFoodProfileNeverWarnings && exp195Warnings.isVariant12;

  const periodicity = useProductPeriodicity(product?.id);
  const neveredTagIds = useNeveredTagIds({
    enabled: enableFoodProfileNeverWarnings,
  });
  const neveredIngredientLookup = useNeveredIngredientProductLookup({
    enabled: useV12IngredientWarnings,
  });

  const dietaryIngredients = useDietaryRestrictionText(product);
  const isTagNever =
    enableFoodProfileNeverWarnings &&
    productContainsNeverTag(product?.tags, neveredTagIds);
  const neverTagName =
    useNamedStackedWarnings && isTagNever
      ? getProductNeverTagName(product?.tags, neveredTagIds)
      : null;
  const neveredIngredientNames =
    useV12IngredientWarnings && product?.id != null
      ? getNeveredIngredientNamesForProducts(
          [product.id],
          neveredIngredientLookup
        )
      : [];
  const neverNames = useNamedStackedWarnings
    ? [
        ...(neverTagName ? [neverTagName] : []),
        ...neveredIngredientNames.filter((name) => name !== neverTagName),
      ]
    : [];

  return resolveItemWarnings({
    dietaryIngredients,
    periodicity,
    hasNever: isTagNever || neveredIngredientNames.length > 0,
    neverTagNames: neverNames,
    stackNeverWithDietary: useNamedStackedWarnings,
  });
}
